import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Heart, Brain, MapPin, Share2, ThumbsUp, X } from "lucide-react";

export default function VotePage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [contests, setContests] = useState([]);
    const [selectedContest, setSelectedContest] = useState("");
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);
    const [votingId, setVotingId] = useState("");
    const [toast, setToast] = useState("");

    useEffect(() => {
        api.get("/contests").then((r) => {
            setContests(r.data.contests || []);
            if (r.data.contests?.length) setSelectedContest(r.data.contests[0].id);
        });
    }, []);

    useEffect(() => {
        if (!selectedContest) return;
        setLoading(true);
        api.get(`/voting/${selectedContest}`).then((r) => {
            setSubmissions(r.data.submissions || []); setLoading(false);
        });
    }, [selectedContest]);

    const vote = async (subId) => {
        if (!user) { navigate("/dang-nhap"); return; }
        setVotingId(subId);
        try {
            await api.post("/votes", { submission_id: subId });
            setSubmissions(submissions.map((s) => s.id === subId ? { ...s, votes_count: (s.votes_count || 0) + 1, _voted: true } : s));
            showToast("Đã ghi nhận lượt vote của bạn.");
        } catch (e) {
            showToast(formatApiError(e.response?.data?.detail), true);
        } finally {
            setVotingId("");
        }
    };

    const share = (sub) => {
        const url = encodeURIComponent(window.location.href);
        const text = encodeURIComponent(`Hãy bình chọn cho bài thi "${sub.user_name}" tại cuộc thi của Cục Dân số!`);
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`, "_blank", "width=600,height=500");
    };

    const showToast = (msg, isError) => {
        setToast({ msg, isError });
        setTimeout(() => setToast(""), 3500);
    };

    return (
        <main className="pt-24 pb-12 min-h-screen">
            <div className="container-x">
                <div className="mb-8 fade-up flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] mb-2">Cộng đồng bình chọn</div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Bình chọn bài thi yêu thích</h1>
                        <p className="text-slate-500 mt-2 text-sm">Vote sẽ là yếu tố bổ trợ cho điểm chính thức do BGK quyết định.</p>
                    </div>
                    <select value={selectedContest} onChange={(e) => setSelectedContest(e.target.value)} className="input max-w-md" data-testid="contest-filter">
                        {contests.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                </div>

                {loading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[1, 2, 3].map((i) => <div key={i} className="card rounded-2xl h-72 shimmer" />)}
                    </div>
                ) : submissions.length === 0 ? (
                    <div className="text-center text-slate-500 py-20">Chưa có bài thi nào để bình chọn.</div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {submissions.map((s) => (
                            <div key={s.id} className="card card-hover p-5 flex flex-col" data-testid={`vote-card-${s.id}`}>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/40 flex items-center justify-center overflow-hidden">
                                        {s.user_avatar ? <img src={s.user_avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-[var(--gold-deep)] text-sm">{s.user_name?.[0]}</span>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-heading font-semibold text-sm truncate">{s.user_name}</div>
                                        <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.user_province || "—"}</div>
                                    </div>
                                    {s.ai_essay_score && (
                                        <span className="badge badge-gold whitespace-nowrap"><Brain className="w-3 h-3" /> {Math.round(s.ai_essay_score)}</span>
                                    )}
                                </div>
                                <p className="text-sm text-slate-600 line-clamp-5 leading-relaxed flex-1">{s.essay_text}</p>
                                <button onClick={() => setOpen(s)} className="text-xs text-[var(--gold-deep)] mt-2 self-start">Đọc đầy đủ →</button>
                                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--border)]">
                                    <span className="text-sm text-pink-400 inline-flex items-center gap-1.5"><Heart className="w-4 h-4 fill-current" /> {s.votes_count || 0}</span>
                                    <div className="flex gap-2">
                                        <button onClick={() => share(s)} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`share-${s.id}`}>
                                            <Share2 className="w-3 h-3" />
                                        </button>
                                        <button onClick={() => vote(s.id)} disabled={votingId === s.id || s._voted}
                                            className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`vote-btn-${s.id}`}>
                                            <ThumbsUp className="w-3 h-3" /> {s._voted ? "Đã vote" : "Vote"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal full essay */}
            {open && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(null)}>
                    <div className="card rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h2 className="font-heading text-xl font-semibold">{open.user_name}</h2>
                                <div className="text-xs text-slate-500 mt-1">{open.user_province}</div>
                            </div>
                            <button onClick={() => setOpen(null)} className="text-slate-500 hover:text-[var(--navy)]"><X className="w-5 h-5" /></button>
                        </div>
                        <p className="text-slate-700 leading-relaxed whitespace-pre-line">{open.essay_text}</p>
                        <button onClick={() => { vote(open.id); setOpen(null); }} className="btn-primary mt-6 w-full" data-testid="modal-vote-btn">
                            <Heart className="w-4 h-4" /> Bình chọn bài này
                        </button>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && (
                <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl ${toast.isError ? "bg-red-500" : "bg-[var(--gold)]"} text-[#0B0A1C] font-semibold shadow-2xl fade-up`}>
                    {toast.msg}
                </div>
            )}
        </main>
    );
}
